import React, { ReactNode } from "react";
import cs from "classnames";
import { Select } from "../Select";
import { SortArrowsIcon } from "../MyAppsTable/icons";
import { DataTableColumn, DataTableSort } from "./types";
import styles from "./cards.module.scss";

type DataCardsProps<T> = {
  columns: DataTableColumn<T>[];
  /** The rows to draw, already sorted by the container. */
  rows: T[];
  rowKey: (row: T, index: number) => React.Key;
  sort?: DataTableSort;
  /** Without it the cards have no sort control. */
  onSortChange?: (sort: DataTableSort) => void;
  onRowClick?: (row: T) => void;
  emptyText?: string;
  /** Keys that lead the card body, in this order; the rest follow. */
  order?: string[];
  /** After the last card: a load-more sentinel or a spinner. */
  footer?: ReactNode;
  className?: string;
};

/**
 * A table on a phone, as the admin panel draws it: a card per row. The title
 * column (an explicit mobile "title", else the pinned one, else the first)
 * heads the card with the "header" cells beside it; "full" cells take a line
 * each, the rest share a grid of label / value pairs, and "footer" cells
 * (actions) close the card without a label. A select-all from a column's
 * header and the sort, as a DS select, sit above the cards.
 */
export function DataCards<T>({
  columns,
  rows,
  rowKey,
  sort = null,
  onSortChange,
  onRowClick,
  emptyText = "No data",
  order = [],
  footer,
  className,
}: DataCardsProps<T>) {
  const titleColumn =
    columns.find((column) => column.mobile === "title") ||
    columns.find((column) => column.fixed) ||
    columns[0];
  const rest = columns.filter(
    (column) => column !== titleColumn && column.mobile !== "hidden",
  );
  const rank = (column: DataTableColumn<T>) => {
    const index = order.indexOf(column.key);
    return index < 0 ? order.length : index;
  };
  const body = rest
    .filter((column) => !column.mobile || column.mobile === "full")
    // Array#sort is stable: unlisted columns keep their own order.
    .sort((a, b) => rank(a) - rank(b));
  const full = body.filter((column) => column.mobile === "full");
  const compact = body.filter((column) => !column.mobile);
  const header = rest.filter((column) => column.mobile === "header");
  const footerCells = rest.filter((column) => column.mobile === "footer");

  const sortable = columns.filter((column) => column.sortable && column.title);
  const headerStarts = columns
    .filter((column) => column.headerStart && column.mobile !== "hidden")
    .map((column) => (
      <React.Fragment key={column.key}>{column.headerStart}</React.Fragment>
    ));
  const showSort =
    Boolean(onSortChange) && sortable.length > 0 && rows.length > 1;

  // Cells that hold their own controls keep the tap from opening the row.
  const stop = (column: DataTableColumn<T>) =>
    column.stopRowClick
      ? (event: React.MouseEvent) => event.stopPropagation()
      : undefined;

  const field = (row: T, column: DataTableColumn<T>, wide = false) => {
    const content = column.render(row);
    return (
      <div
        key={column.key}
        data-col={column.key}
        className={cs(styles.field, { [styles.full]: wide })}
        onClick={stop(column)}
      >
        <span className={styles.label}>{column.title}</span>
        <span className={styles.value}>
          {content == null || content === "" ? "—" : content}
        </span>
      </div>
    );
  };

  const bare = (row: T, column: DataTableColumn<T>) => (
    <span key={column.key} data-col={column.key} onClick={stop(column)}>
      {column.render(row)}
    </span>
  );

  return (
    <div className={cs(styles.cards, className)}>
      {(showSort || headerStarts.length > 0) && (
        <div className={styles.toolbar}>
          {headerStarts}
          {showSort && (
            <div className={styles.sortRow}>
              <Select
                className={styles.sortSelect}
                aria-label="Sort by"
                placeholder="Sort by"
                value={sort?.key ?? ""}
                options={sortable.map((column) => ({
                  value: column.key,
                  label: `Sort by ${column.title}`,
                }))}
                onChange={(key) =>
                  onSortChange?.({
                    key,
                    direction: sort?.key === key ? sort.direction : "asc",
                  })
                }
              />
              <button
                type="button"
                className={styles.directionButton}
                disabled={!sort}
                onClick={() =>
                  sort &&
                  onSortChange?.({
                    ...sort,
                    direction: sort.direction === "asc" ? "desc" : "asc",
                  })
                }
                aria-label={
                  sort?.direction === "desc"
                    ? "Sorted descending"
                    : "Sorted ascending"
                }
              >
                <SortArrowsIcon
                  className={styles.directionIcon}
                  upClassName={
                    sort?.direction === "asc" ? styles.sortActive : undefined
                  }
                  downClassName={
                    sort?.direction === "desc" ? styles.sortActive : undefined
                  }
                />
              </button>
            </div>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <p className={styles.empty}>{emptyText}</p>
      ) : (
        <div className={styles.list} role="list">
          {rows.map((row, index) => (
            <div
              key={rowKey(row, index)}
              className={cs(styles.card, {
                [styles.clickable]: Boolean(onRowClick),
              })}
              role="listitem"
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              <div className={styles.cardHeader}>
                <span
                  className={styles.title}
                  data-col={titleColumn.key}
                  onClick={stop(titleColumn)}
                >
                  {titleColumn.mobile !== "title" && titleColumn.title && (
                    <span className={styles.titleLabel}>
                      {titleColumn.title}
                    </span>
                  )}
                  {titleColumn.render(row)}
                </span>
                {header.length > 0 && (
                  <span className={styles.headerCells}>
                    {header.map((column) => bare(row, column))}
                  </span>
                )}
              </div>
              {(full.length > 0 || compact.length > 0) && (
                <div className={styles.grid}>
                  {full.map((column) => field(row, column, true))}
                  {compact.map((column) => field(row, column))}
                </div>
              )}
              {footerCells.length > 0 && (
                <div className={styles.footer}>
                  {footerCells.map((column) => bare(row, column))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {footer}
    </div>
  );
}

export default DataCards;
